import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import FilterListIcon from "@mui/icons-material/FilterList";
import { toast } from "react-toastify";
import { getVisitorLogs, getVisitorLogStats, exportVisitorLogs, formatApiDate, formatDisplayDateTime, locationFilterParams } from "../../api/superAdminApi";
import { useLocationCascading } from "./hooks/useLocationCascading";
import { useAdminPanel } from "./hooks/useAdminPanel";
import SuperAdminLocationSelects from "./SuperAdminLocationSelects";
import SuperAdminPageHeader from "./SuperAdminPageHeader";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];
const LOG_TYPES = ["", "Guest", "Delivery"];

export default function VMSuperAdminVisitorLogs() {
  const { adminScope, locationLocked } = useAdminPanel();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [searchKey, setSearchKey] = useState("");
  const [logType, setLogType] = useState("");
  const [typeStats, setTypeStats] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const loc = useLocationCascading();

  const buildFilters = useCallback(
    (pageOverride) => {
      const params = {
        page: pageOverride !== undefined ? pageOverride : page,
        no_of_rows: rowsPerPage,
        ...locationFilterParams(loc, adminScope, { locked: locationLocked }),
      };
      const from = formatApiDate(fromDate);
      const to = formatApiDate(toDate);
      if (from) params.from = from;
      if (to) params.to = to;
      if (searchKey.trim()) params.search = searchKey.trim();
      if (logType) params.type = logType;
      return params;
    },
    [page, rowsPerPage, fromDate, toDate, searchKey, logType, loc, adminScope, locationLocked]
  );

  const loadData = useCallback(
    async (pageOverride) => {
      setLoading(true);
      const filters = buildFilters(pageOverride);
      try {
        const [statsRes, listRes] = await Promise.all([getVisitorLogStats(filters), getVisitorLogs(filters)]);
        setTypeStats(statsRes.data?.data?.items || []);
        setRows(listRes.data?.data || []);
        setTotalRecords(listRes.data?.totalCount ?? 0);
      } catch {
        setTypeStats([]);
        setRows([]);
        setTotalRecords(0);
        toast.info("Visitor logs API unavailable.");
      } finally {
        setLoading(false);
      }
    },
    [buildFilters]
  );

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <SuperAdminPageHeader
        title="Visitor Log Register"
        actions={
          <Button variant={filtersOpen ? "contained" : "outlined"} startIcon={<FilterListIcon />} onClick={() => setFiltersOpen((o) => !o)}>
            Filters
          </Button>
        }
      />

      {typeStats.length > 0 ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {typeStats.map((item) => (
            <Grid item xs={6} sm={4} md={3} key={item.log_type}>
              <Paper sx={{ p: 2, textAlign: "center", bgcolor: "primary.main", color: "primary.contrastText" }}>
                <Typography variant="body2">{item.log_type}</Typography>
                <Typography variant="statValue" sx={{ fontWeight: 700 }}>{item.count ?? 0}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : null}

      {filtersOpen ? (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker label="From" value={fromDate} onChange={setFromDate} format="DD/MM/YYYY" slotProps={{ textField: { size: "small", fullWidth: true } }} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker label="To" value={toDate} onChange={setToDate} format="DD/MM/YYYY" slotProps={{ textField: { size: "small", fullWidth: true } }} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Log Type</InputLabel>
                  <Select label="Log Type" value={logType} onChange={(e) => setLogType(e.target.value)}>
                    <MenuItem value="">All</MenuItem>
                    {LOG_TYPES.filter(Boolean).map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <SuperAdminLocationSelects {...loc} locationLocked={locationLocked} lockedScope={adminScope} itemProps={{ xs: 12, sm: 6, md: 4 }} />
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField fullWidth size="small" label="Search" value={searchKey} onChange={(e) => setSearchKey(e.target.value)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={() => { setPage(0); loadData(0); }} disabled={loading}>{loading ? <CircularProgress size={22} color="inherit" /> : "Search"}</Button>
                  <Button variant="outlined" onClick={() => exportVisitorLogs(buildFilters())}>Export</Button>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      ) : null}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "primary.main" }}>
              {["#", "Name", "Type", "Phone", "Vehicle", "Check in", "Check out", "Venue", "City"].map((h) => (
                <TableCell key={h} sx={{ color: "primary.contrastText", fontWeight: 700 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={28} aria-label="Loading visitor logs" /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}>No visitor logs found.</TableCell></TableRow>
            ) : (
              rows.map((row, idx) => (
                <TableRow key={row.id ?? idx}>
                  <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                  <TableCell>{row.name ?? "—"}</TableCell>
                  <TableCell>{row.type ?? "—"}</TableCell>
                  <TableCell>{row.phone_no ?? "—"}</TableCell>
                  <TableCell>{row.vehicle_no ?? "—"}</TableCell>
                  <TableCell>{formatDisplayDateTime(row.check_in)}</TableCell>
                  <TableCell>{formatDisplayDateTime(row.check_out)}</TableCell>
                  <TableCell>{row.venue_name ?? "—"}</TableCell>
                  <TableCell>{row.city_name ?? "—"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS} count={totalRecords} page={page} rowsPerPage={rowsPerPage} onPageChange={(_, p) => { setPage(p); loadData(p); }} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); loadData(0); }} colSpan={9} />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
}
